import React, { ReactElement, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox } from '@milanote/roma';

import ErrorToast from '../../../../../components/toast/ErrorToast';
import HttpStatus from 'http-status-codes';

import {
    getErrorDisplayMessageFromResponse,
    getErrorMessageFromResponse,
    getResponseStatusCode,
} from '../../../../../utils/errorUtil';
import { fetchImageBlobFromUrl } from '../../content/utils/imageModalImageBlobUtils';
import { getBackgroundRemovedFilename } from './imageModalBackgroundRemovalFormatUtils';
import { removeBackground, uploadProcessedImage } from './imageModalBackgroundRemovalService';
import { updateElement } from '../../../../actions/elementActions';
import type { ImMNElement, MNImageContent } from '../../../../../../common/elements/elementModelTypes';
import {
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
} from './imageModalBackgroundRemovalProgressTypes';
import { buildBackgroundRemovalCacheChanges, hasBackgroundRemovalCache } from './imageModalBackgroundRemovalUtils';

const BACKGROUND_REMOVAL_ERROR_MESSAGE = 'You have exhausted your monthly usage limit';
const GENERIC_BACKGROUND_REMOVAL_ERROR_MESSAGE = 'Background removal failed';

type ImageModalBackgroundRemovalButtonProps = {
    element: ImMNElement;
    elementId: string;
    cropSourceImage: MNImageContent;
    largestImageUrl: string;
    imageType: string;
    progress: BackgroundRemovalProgress | null;
    setProgress: (progress: BackgroundRemovalProgress | null) => void;
    disabled: boolean;
    // Whether the removed-background variant is staged to be applied when the user saves. The processed
    // image is cached on the element immediately, but the variant is only applied on save.
    stagedActive: boolean;
    onChangeStagedActive: (active: boolean) => void;
};

/**
 * Provide server-backed background removal. The processed image is cached immediately to avoid repeat
 * provider calls, while applying the removed-background variant is staged until the user saves.
 */
const ImageModalBackgroundRemovalButton: React.FC<ImageModalBackgroundRemovalButtonProps> = ({
    element,
    elementId,
    cropSourceImage,
    largestImageUrl,
    imageType,
    progress,
    setProgress,
    disabled,
    stagedActive,
    onChangeStagedActive,
}): ReactElement => {
    const dispatch = useDispatch();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleFailure = (error: unknown) => {
        // The request uses an arraybuffer response, so the JSON error body isn't readable here — detect the
        // usage limit via the 429 status the server tags it with instead.
        if (getResponseStatusCode(error) === HttpStatus.TOO_MANY_REQUESTS) {
            return setErrorMessage(BACKGROUND_REMOVAL_ERROR_MESSAGE);
        }

        const message =
            getErrorDisplayMessageFromResponse(error) ||
            getErrorMessageFromResponse(error) ||
            (error instanceof Error ? error.message : GENERIC_BACKGROUND_REMOVAL_ERROR_MESSAGE);

        setErrorMessage(message);
    };

    const removeBackgroundFromImage = async () => {
        setErrorMessage(null);
        setProgress(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

        try {
            const imageBlob = await fetchImageBlobFromUrl(largestImageUrl);
            const fileName = getBackgroundRemovedFilename(largestImageUrl, imageType, imageBlob);
            const processedBlob = await removeBackground(imageBlob, fileName, setProgress);

            const withoutBackgroundSource = await uploadProcessedImage({
                dispatch,
                elementId,
                fileName,
                blob: processedBlob,
            });

            // Persist only the processed source cache so the provider is never called again. The visible
            // image is left untouched; applying the removed-background variant is deferred until save.
            dispatch(
                updateElement({
                    id: elementId,
                    changes: buildBackgroundRemovalCacheChanges({
                        withBackgroundSource: cropSourceImage,
                        withoutBackgroundSource,
                    }),
                }),
            );

            onChangeStagedActive(true);
        } catch (error) {
            handleFailure(error);
        } finally {
            setProgress(null);
        }
    };

    const handleButtonClick = () => {
        if (stagedActive) return onChangeStagedActive(false);

        if (hasBackgroundRemovalCache(element)) return onChangeStagedActive(true);

        return removeBackgroundFromImage();
    };

    const isProcessing = !!progress;
    const isDisabled = disabled || isProcessing;
    const title = stagedActive ? 'Apply background' : 'Remove background';

    return (
        <>
            <Button
                color="tertiary"
                className="ImageModalBackgroundRemovalButton whitespace-nowrap"
                onClick={handleButtonClick}
                isDisabled={isDisabled}
                title={title}
            >
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    <Checkbox.Base
                        className="pointer-events-none"
                        variant="form"
                        size="sm"
                        isSelected={stagedActive}
                        isDisabled={isDisabled}
                    />
                    <span>{title}</span>
                </span>
            </Button>
            <ErrorToast
                show={!!errorMessage}
                message={errorMessage}
                groupId="ImageModalV2"
                onDismiss={() => setErrorMessage(null)}
            />
        </>
    );
};

export default ImageModalBackgroundRemovalButton;
